import React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

/**
 * AdSenseSparkline — inline SVG trend chart for the last N AdSense weekly
 * snapshots. No chart library — hand-rolled path + area fill + tooltip.
 *
 * Props:
 *   points     — [{ week_end, estimated_revenue_zmw }, ...] oldest → newest
 *   deltaPct   — number returned by /api/admin/adsense/trend (positive = up)
 *   width      — SVG viewBox width (default 200)
 *   height     — SVG viewBox height (default 48)
 */
export default function AdSenseSparkline({ points = [], deltaPct = 0, width = 200, height = 48 }) {
  if (!points || points.length < 2) {
    return (
      <div className="text-[11px] text-slate-400" data-testid="adsense-sparkline-empty">
        Sparkline appears once 2+ weekly snapshots exist.
      </div>
    );
  }

  const values = points.map((p) => Number(p.estimated_revenue_zmw) || 0);
  const max = Math.max(...values, 1);   // floor at 1 so a run of zeros doesn't nuke the path
  const min = Math.min(...values, 0);
  const range = max - min || 1;

  const padX = 4;
  const padY = 4;
  const stepX = points.length > 1 ? (width - padX * 2) / (points.length - 1) : 0;
  const coords = values.map((v, i) => {
    const x = padX + i * stepX;
    const y = padY + (height - padY * 2) * (1 - (v - min) / range);
    return [x, y];
  });

  const linePath = coords.map(([x, y], i) => (i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`)).join(" ");
  const areaPath = `${linePath} L ${coords[coords.length - 1][0]} ${height - padY} L ${coords[0][0]} ${height - padY} Z`;

  const [lastX, lastY] = coords[coords.length - 1];
  const trendUp = deltaPct > 0.5;
  const trendDown = deltaPct < -0.5;
  const TrendIcon = trendUp ? TrendingUp : trendDown ? TrendingDown : Minus;
  const trendColor = trendUp ? "text-forest" : trendDown ? "text-rose-500" : "text-slate-500";
  const strokeColor = trendUp ? "#0f3d2e" : trendDown ? "#e11d48" : "#64748b";
  const fillColor = trendUp ? "rgba(15,61,46,.12)" : trendDown ? "rgba(225,29,72,.10)" : "rgba(100,116,139,.10)";

  return (
    <div className="flex items-center gap-3" data-testid="adsense-sparkline">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        preserveAspectRatio="none"
        className="overflow-visible"
        aria-label={`AdSense revenue trend over the last ${points.length} weeks`}
      >
        <path d={areaPath} fill={fillColor} stroke="none" />
        <path d={linePath} fill="none" stroke={strokeColor} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        {/* Marker on the latest point */}
        <circle cx={lastX} cy={lastY} r="2.6" fill={strokeColor} />
        {/* Weekly tick markers hidden but present for accessibility */}
        {coords.map(([x, y], i) => (
          <title key={i}>{`${points[i].week_end}: K${Number(points[i].estimated_revenue_zmw).toFixed(2)}`}</title>
        ))}
      </svg>
      <div className={`flex items-baseline gap-1 text-xs ${trendColor}`} data-testid="adsense-trend-delta">
        <TrendIcon className="h-3 w-3" />
        <span className="font-semibold">
          {deltaPct > 0 ? "+" : ""}{deltaPct.toFixed(1)}%
        </span>
        <span className="text-slate-500 text-[10px]">{points.length}-wk</span>
      </div>
    </div>
  );
}
